import React, { useState, useEffect, useCallback } from 'react';
import { FoxSpinnerInline } from '../ui/FoxSpinner';
import axios from 'axios';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Search, ShieldCheck, Archive, Download, Pencil, History, Trash2, RotateCcw, Users, Send, Check } from 'lucide-react';
import { toast } from 'sonner';
import { CountrySelect } from '../CountrySelect';
import { fetchFiscalLabels, docInfo, docValid } from '../../lib/fiscalDocs';
import { Checkbox } from '../ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { ClientPasswordCard } from './ClientPasswordCard';

const MOTIVOS_EXCLUSAO = [
  'Dados falsos ou inválidos',
  'Cadastro duplicado',
  'Pedido do próprio cliente',
  'Suspeita de fraude',
  'Teste interno',
  'Outro',
];

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('access_token') || sessionStorage.getItem('access_token')}`,
});

const copyCell = (value, label) => {
  navigator.clipboard.writeText(value || '');
  toast.success(`${label} copiado!`, { duration: 1500 });
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString('pt-BR'); } catch { return '—'; }
};

const CAMPOS = {
  name: 'Nome', cpf: 'CPF', is_foreigner: 'Conta internacional', country: 'País',
  fiscal_document: 'Documento fiscal', whatsapp: 'WhatsApp', whatsapp_dial: 'DDI',
  email: 'E-mail', birth_date: 'Nascimento', nickname: 'Apelido',
  status_cadastro: 'Situação do cadastro',
};

const showVal = (v) => {
  if (v === true) return 'sim';
  if (v === false) return 'não';
  if (v === null || v === undefined || v === '') return 'vazio';
  return String(v);
};

const exportCsv = (rows, filename) => {
  if (!rows.length) { toast.error('Nada para exportar'); return; }
  const headers = Object.keys(rows[0]);
  const csv = [headers.join(','),
    ...rows.map((r) => headers.map((h) => `"${r[h] ?? ''}"`).join(','))].join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  link.click();
  toast.success(`${rows.length} clientes exportados!`);
};

const ClientsManagement = () => {
  const [verified, setVerified] = useState([]);
  const [legacy, setLegacy] = useState([]);
  const [deleted, setDeleted] = useState([]);
  const [deleting, setDeleting] = useState(null);   // cliente a excluir
  const [delForm, setDelForm] = useState({ confirm: '', reason: '', preset: '' });
  const [removing, setRemoving] = useState(false);
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [editClient, setEditClient] = useState(null); // cliente em edição
  const [editForm, setEditForm] = useState({});
  const [historyOf, setHistoryOf] = useState(null);
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [fiscalLabels, setFiscalLabels] = useState(null);
  const [inviteFilter, setInviteFilter] = useState('all');   // all | pending | sent
  const [saving, setSaving] = useState(false);

  const openEdit = (c) => {
    setEditClient(c);
    setEditForm({
      name: c.name || '',
      cpf: c.cpf || '',
      is_foreigner: !!c.is_foreigner,
      country: c.country || '',
      fiscal_document: c.fiscal_document || '',
      whatsapp: c.whatsapp || '',
      whatsapp_dial: String(c.whatsapp_dial || '55'),
      email: c.email || '',
      nickname: c.nickname || '',
      note: '',
      birth_date: c.birth_date
        ? c.birth_date.split('-').reverse().join('/')
        : '',
    });
    fetchFiscalLabels().then(setFiscalLabels);
  };

  const loadHistory = async (cliente) => {
    if (!cliente) return;
    setHistoryOf(cliente);
    setHistory([]);
    setLoadingHistory(true);
    try {
      const r = await axios.get(
        `${BACKEND_URL}/api/customers/verified/${cliente.id}/history`,
        { headers: authHeaders() });
      setHistory(r.data.events || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao carregar o histórico');
    }
    setLoadingHistory(false);
  };

  const saveEdit = async () => {
    if (!editClient) return;
    if (editForm.is_foreigner) {
      const regra = docInfo(fiscalLabels, editForm.country);
      if (!editForm.country) { toast.error('Escolha o país do cliente.'); return; }
      if (!docValid(fiscalLabels, editForm.country, editForm.fiscal_document)) {
        toast.error(`${regra.label} inválido. Formato aceito: ${regra.hint}.`); return;
      }
    }
    setSaving(true);
    try {
      const payload = { is_foreigner: !!editForm.is_foreigner };
      const obrigatorios = editForm.is_foreigner
        ? ['name', 'country', 'fiscal_document', 'whatsapp', 'whatsapp_dial']
        : ['name', 'cpf', 'whatsapp', 'whatsapp_dial'];
      obrigatorios.forEach((k) => {
        if ((editForm[k] || '').trim()) payload[k] = editForm[k].trim();
      });
      // campos que o admin pode LIMPAR: envia string vazia de propósito
      ['email', 'nickname', 'birth_date'].forEach((k) => {
        payload[k] = (editForm[k] || '').trim();
      });
      if ((editForm.note || '').trim()) payload.note = editForm.note.trim();
      await axios.put(`${BACKEND_URL}/api/customers/verified/${editClient.id}`,
        payload, { headers: authHeaders() });
      toast.success('Dados do cliente atualizados!');
      setEditClient(null);
      loadAll(search);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao salvar');
    }
    setSaving(false);
  };

  const loadAll = useCallback(async (searchTerm = '') => {
    setLoading(true);
    try {
      const q = searchTerm ? `?search=${encodeURIComponent(searchTerm)}` : '';
      const [v, l, p, d] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/customers/verified${q}`, { headers: authHeaders() }),
        axios.get(`${BACKEND_URL}/api/customers/${q}`, { headers: authHeaders() }),
        axios.get(`${BACKEND_URL}/api/customers/migration-progress`, { headers: authHeaders() }),
        axios.get(`${BACKEND_URL}/api/customers/deleted${q}`, { headers: authHeaders() }),
      ]);
      setVerified(v.data);
      setLegacy(l.data);
      setProgress(p.data);
      setDeleted(d.data);
    } catch {
      toast.error('Erro ao carregar clientes');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const confirmDelete = async () => {
    if (!deleting) return;
    setRemoving(true);
    try {
      const r = await axios.delete(
        `${BACKEND_URL}/api/customers/verified/${deleting.id}`,
        { headers: authHeaders(), data: { confirm_kako_id: delForm.confirm.trim(), reason: delForm.reason.trim() } });
      toast.success(`Cadastro ${r.data.kako_id} excluído. ${r.data.orders_preserved} pedido(s) preservado(s).`);
      setDeleting(null);
      setEditClient(null);
      loadAll(search);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao excluir cadastro');
    }
    setRemoving(false);
  };

  const restoreClient = async (cliente) => {    try {
      await axios.post(`${BACKEND_URL}/api/customers/deleted/${cliente.id}/restore`,
        {}, { headers: authHeaders() });
      toast.success(`Cadastro ${cliente.kako_id} restaurado!`);
      loadAll(search);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao restaurar cadastro');
    }
  };

  const toggleInvite = async (cliente) => {
    const cpf = cliente.customer_cpf;
    const novo = !cliente.invite_sent;
    try {
      const r = await axios.post(`${BACKEND_URL}/api/customers/legacy/${cpf}/invite`,
        { sent: novo }, { headers: authHeaders() });
      setLegacy((lista) => lista.map((c) => (c.customer_cpf === cpf
        ? { ...c, invite_sent: novo, invite_sent_at: r.data.sent_at } : c)));
      toast.success(novo ? 'Marcado como link enviado!' : 'Marca removida');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao marcar');
    }
  };

  const exportVerified = () => exportCsv(verified.map((c) => ({
    'KAKO ID': c.kako_id, 'Nickname': c.nickname || '', 'Nome': c.name,
    'CPF / Documento': c.document_display || c.cpf_formatted,
    'País': c.is_foreigner ? (c.country || '') : 'Brasil',
    'WhatsApp': c.whatsapp, 'Email': c.email || '',
    'Cadastro': fmtDate(c.created_at), 'Pedidos': c.total_orders,
    'Moedas': c.total_coins, 'Total Gasto (R$)': (c.total_amount || 0).toFixed(2),
    'Última Compra': fmtDate(c.last_purchase),
  })), `clientes_verificados_${new Date().toISOString().split('T')[0]}.csv`);

  const exportLegacy = () => exportCsv(legacy.map((c) => ({
    'CPF': c.customer_cpf_formatted || c.customer_cpf || '', 'Nome': c.customer_name || '',
    'WhatsApp': c.customer_whatsapp || '', 'KAKO ID': c.kako_id || '',
    'Verificado': c.verified ? 'SIM' : 'NÃO',
    'Link enviado': c.invite_sent ? `SIM (${fmtDate(c.invite_sent_at)})` : 'NÃO',
    'Pedidos': c.total_orders || 0, 'Moedas': c.total_coins || 0,
    'Total Gasto (R$)': (c.total_amount || 0).toFixed(2),
  })), `clientes_legado_${new Date().toISOString().split('T')[0]}.csv`);

  const legacyEnviados = legacy.filter((c) => c.invite_sent).length;
  const legacyFiltrado = legacy.filter((c) => (
    inviteFilter === 'all' || (inviteFilter === 'sent' ? c.invite_sent : !c.invite_sent)));

  if (loading) {
    return <div className="flex justify-center p-8"><FoxSpinnerInline size="lg" /></div>;
  }

  return (
    <div className="space-y-4" data-testid="clients-management">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="text-2xl font-bold">👥 Clientes</h2>
          <p className="text-sm text-gray-600">
            {verified.length} verificados · {legacy.length} na lista antiga
          </p>
        </div>
        <div className="flex gap-2">
          <Input
            placeholder="Buscar nome, CPF, documento, país, email, KAKO ID..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && loadAll(search)}
            className="w-64"
            data-testid="clients-search-input"
          />
          <Button onClick={() => loadAll(search)} className="bg-purple-600" data-testid="clients-search-button">
            <Search className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {progress && (
        <Card className="border-green-200 bg-green-50/50" data-testid="migration-progress-card">
          <CardContent className="py-3">
            <div className="flex items-center justify-between text-sm mb-1.5">
              <span className="font-semibold text-green-800 flex items-center gap-1.5">
                <ShieldCheck className="w-4 h-4" /> Migração para contas verificadas
              </span>
              <span className="text-green-700 font-bold">{progress.percent}%</span>
            </div>
            <div className="w-full bg-green-100 rounded-full h-2.5">
              <div className="bg-green-500 h-2.5 rounded-full transition-all"
                style={{ width: `${Math.min(progress.percent, 100)}%` }} />
            </div>
            <p className="text-xs text-gray-600 mt-1.5">
              {progress.legacy_covered} de {progress.legacy_customers} clientes da lista antiga já criaram
              conta verificada · {progress.verified_accounts} contas no total
            </p>
          </CardContent>
        </Card>
      )}

      <Tabs defaultValue="verified">
        <TabsList>
          <TabsTrigger value="verified" data-testid="tab-verified-clients">
            <ShieldCheck className="w-4 h-4 mr-1.5" /> Verificados ({verified.length})
          </TabsTrigger>
          <TabsTrigger value="legacy" data-testid="tab-legacy-clients">
            <Archive className="w-4 h-4 mr-1.5" /> Lista antiga ({legacy.length})
          </TabsTrigger>
          <TabsTrigger value="deleted" data-testid="tab-deleted-clients">
            <Trash2 className="w-4 h-4 mr-1.5" /> Lixeira ({deleted.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="verified" className="space-y-2 mt-3">
          <div className="flex justify-end">
            <Button onClick={exportVerified} variant="outline" size="sm" data-testid="export-verified-button">
              <Download className="h-4 w-4 mr-1.5" /> Exportar CSV
            </Button>
          </div>
          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="verified-clients-table">
                  <thead className="bg-green-50 border-b">
                    <tr>
                      <th className="text-left p-3 font-semibold">KAKO ID</th>
                      <th className="text-left p-3 font-semibold">Nome</th>
                      <th className="text-left p-3 font-semibold">CPF / Documento</th>
                      <th className="text-left p-3 font-semibold">WhatsApp</th>
                      <th className="text-left p-3 font-semibold">Email</th>
                      <th className="text-left p-3 font-semibold">Cadastro</th>
                      <th className="text-right p-3 font-semibold">Pedidos</th>
                      <th className="text-right p-3 font-semibold">Total Gasto</th>
                      <th className="text-left p-3 font-semibold">Última Compra</th>
                      <th className="text-center p-3 font-semibold">Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {verified.length === 0 ? (
                      <tr><td colSpan="10" className="text-center py-8 text-gray-500" data-testid="verified-empty">
                        Nenhuma conta verificada ainda. Elas aparecem aqui conforme os clientes se cadastram.
                      </td></tr>
                    ) : verified.map((c) => (
                      <tr key={c.id} className="border-b hover:bg-green-50/40">
                        <td className="p-3 font-bold text-purple-600 cursor-pointer"
                          onClick={() => copyCell(c.kako_id, 'KAKO ID')} title="Copiar">{c.kako_id}</td>
                        <td className="p-3 font-medium cursor-pointer"
                          onClick={() => copyCell(c.name, 'Nome')} title="Copiar">
                          {c.name}
                          {c.holder_count > 1 && (
                            <span
                              className="ml-1.5 inline-flex items-center gap-1 rounded-full bg-purple-100 px-1.5 py-0.5 align-middle text-[10px] font-bold text-purple-700"
                              data-testid={`holder-badge-${c.kako_id}`}
                              title={`Mesmo titular em ${c.holder_count} contas KAKO: ${
                                (c.holder_accounts || []).map((h) => h.kako_id).join(', ')}`}>
                              <Users className="h-3 w-3" /> {c.holder_count} contas
                            </span>
                          )}
                          {c.nickname && <span className="block text-xs text-gray-400">{c.nickname}</span>}
                        </td>
                        <td className="p-3 font-mono text-xs text-blue-600 cursor-pointer"
                          onClick={() => copyCell(c.is_foreigner ? c.fiscal_document : c.cpf, c.document_label || 'CPF')}
                          title="Copiar" data-testid={`client-doc-${c.kako_id}`}>
                          {c.document_display || c.cpf_formatted || '—'}
                        </td>
                        <td className="p-3 text-gray-600 cursor-pointer"
                          onClick={() => copyCell(c.whatsapp, 'WhatsApp')} title="Copiar">{c.whatsapp}</td>
                        <td className="p-3 text-gray-600 cursor-pointer max-w-[180px] truncate"
                          onClick={() => copyCell(c.email, 'Email')} title="Copiar">{c.email || '—'}</td>
                        <td className="p-3 text-gray-500 text-xs">{fmtDate(c.created_at)}</td>
                        <td className="p-3 text-right"><Badge variant="secondary">{c.total_orders}</Badge></td>
                        <td className="p-3 text-right font-bold text-green-600">
                          R$ {(c.total_amount || 0).toFixed(2)}
                        </td>
                        <td className="p-3 text-gray-500 text-xs">{fmtDate(c.last_purchase)}</td>
                        <td className="p-3 text-center">
                          <div className="flex items-center justify-center gap-1">
                            <Button size="sm" variant="outline" className="h-7 px-2"
                              data-testid={`edit-client-${c.kako_id}`}
                              onClick={() => openEdit(c)} title="Editar dados">
                              <Pencil className="w-3.5 h-3.5" />
                            </Button>
                            <Button size="sm" variant="outline" title="Excluir cadastro"
                              className="h-7 px-2 border-red-200 text-red-600 hover:bg-red-50"
                              data-testid={`delete-client-${c.kako_id}`}
                              onClick={() => { setDeleting(c); setDelForm({ confirm: '', reason: '', preset: '' }); }}>
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="legacy" className="space-y-2 mt-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="text-xs text-gray-500">
              📋 Lista antiga: montada pelo que foi digitado nas compras (dados não confiáveis).
              Será aposentada conforme a migração avança.
            </p>
            <Button onClick={exportLegacy} variant="outline" size="sm" data-testid="export-legacy-button">
              <Download className="h-4 w-4 mr-1.5" /> Exportar CSV
            </Button>
          </div>
          <div className="flex flex-wrap items-center gap-2 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2"
            data-testid="invite-control-bar">
            <Send className="h-4 w-4 text-blue-600" />
            <p className="text-xs text-blue-900">
              <b>Link de cadastro:</b> {legacyEnviados} já receberam · {legacy.length - legacyEnviados} faltam
            </p>
            <div className="ml-auto flex gap-1">
              {[['all', 'Todos'], ['pending', 'Falta enviar'], ['sent', 'Já enviei']].map(([v, label]) => (
                <Button key={v} size="sm" variant={inviteFilter === v ? 'default' : 'outline'}
                  className={`h-7 px-2.5 text-xs ${inviteFilter === v ? 'bg-blue-600 hover:bg-blue-700' : ''}`}
                  onClick={() => setInviteFilter(v)} data-testid={`invite-filter-${v}`}>
                  {label}
                </Button>
              ))}
            </div>
          </div>
          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="legacy-clients-table">
                  <thead className="bg-gray-100 border-b">
                    <tr>
                      <th className="text-left p-3 font-semibold">#</th>
                      <th className="text-left p-3 font-semibold">Nome</th>
                      <th className="text-left p-3 font-semibold">CPF</th>
                      <th className="text-left p-3 font-semibold">WhatsApp</th>
                      <th className="text-left p-3 font-semibold">KAKO ID</th>
                      <th className="text-center p-3 font-semibold">Conta</th>
                      <th className="text-center p-3 font-semibold">Link de cadastro</th>
                      <th className="text-right p-3 font-semibold">Pedidos</th>
                      <th className="text-right p-3 font-semibold">Moedas</th>
                      <th className="text-right p-3 font-semibold">Total Gasto</th>
                    </tr>
                  </thead>
                  <tbody>
                    {legacyFiltrado.length === 0 ? (
                      <tr><td colSpan="10" className="text-center py-8 text-gray-500" data-testid="legacy-empty">
                        Nenhum cliente encontrado
                      </td></tr>
                    ) : legacyFiltrado.map((customer, index) => (
                      <tr key={customer.customer_cpf} className="border-b hover:bg-gray-50">
                        <td className="p-3">{index + 1}</td>
                        <td className="p-3 font-medium cursor-pointer"
                          onClick={() => copyCell(customer.customer_name, 'Nome')} title="Copiar">
                          {customer.customer_name}
                        </td>
                        <td className="p-3 text-blue-600 font-mono text-xs cursor-pointer"
                          onClick={() => copyCell(customer.customer_cpf, 'CPF')} title="Copiar">
                          {customer.customer_cpf_formatted}
                        </td>
                        <td className="p-3 text-gray-600 cursor-pointer"
                          onClick={() => copyCell(customer.customer_whatsapp, 'WhatsApp')} title="Copiar">
                          {customer.customer_whatsapp}
                        </td>
                        <td className="p-3 text-purple-600 font-bold cursor-pointer"
                          onClick={() => copyCell(customer.kako_id, 'KAKO ID')} title="Copiar">
                          {customer.kako_id}
                        </td>
                        <td className="p-3 text-center">
                          {customer.verified ? (
                            <Badge className="bg-green-100 text-green-700">✅ Verificado</Badge>
                          ) : (
                            <Badge variant="outline" className="text-gray-400">—</Badge>
                          )}
                        </td>
                        <td className="p-3 text-center">
                          {!customer.customer_cpf ? (
                            <span className="text-xs text-gray-300">—</span>
                          ) : customer.invite_sent ? (
                            <button onClick={() => toggleInvite(customer)}
                              data-testid={`invite-sent-${customer.customer_cpf}`}
                              title={`Enviado em ${fmtDate(customer.invite_sent_at)} · clique para desmarcar`}
                              className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-1 text-[11px] font-bold text-green-700 hover:bg-green-200">
                              <Check className="h-3 w-3" /> Enviado
                              <span className="font-normal text-green-600">
                                {customer.invite_sent_at
                                  ? new Date(customer.invite_sent_at).toLocaleDateString('pt-BR')
                                  : ''}
                              </span>
                            </button>
                          ) : (
                            <Button size="sm" variant="outline" className="h-7 px-2 text-[11px]"
                              data-testid={`invite-send-${customer.customer_cpf}`}
                              onClick={() => toggleInvite(customer)} title="Marcar que já mandei o link">
                              <Send className="mr-1 h-3 w-3" /> Marcar enviado
                            </Button>
                          )}
                        </td>
                        <td className="p-3 text-right"><Badge variant="secondary">{customer.total_orders}</Badge></td>
                        <td className="p-3 text-right font-bold text-purple-600">
                          {(customer.total_coins || 0).toLocaleString()} 🪙
                        </td>
                        <td className="p-3 text-right font-bold text-green-600">
                          R$ {(customer.total_amount || 0).toFixed(2)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="deleted" className="space-y-2 mt-3">
          <p className="text-xs text-gray-500">
            🗑️ Cadastros excluídos pelo admin. O KAKO ID volta a ficar livre para um novo
            cadastro e os pedidos antigos continuam intactos no financeiro. Dá para restaurar
            enquanto ninguém tiver cadastrado o mesmo KAKO ID de novo.
          </p>
          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="deleted-clients-table">
                  <thead className="bg-red-50 border-b">
                    <tr>
                      <th className="text-left p-3 font-semibold">KAKO ID</th>
                      <th className="text-left p-3 font-semibold">Nome</th>
                      <th className="text-left p-3 font-semibold">CPF / Documento</th>
                      <th className="text-left p-3 font-semibold">WhatsApp</th>
                      <th className="text-left p-3 font-semibold">Motivo</th>
                      <th className="text-left p-3 font-semibold">Excluído em</th>
                      <th className="text-center p-3 font-semibold">Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {deleted.length === 0 ? (
                      <tr><td colSpan="7" className="text-center py-8 text-gray-500" data-testid="deleted-empty">
                        Nenhum cadastro excluído.
                      </td></tr>
                    ) : deleted.map((c) => (
                      <tr key={c.id} className="border-b hover:bg-red-50/40">
                        <td className="p-3 font-bold text-purple-600 cursor-pointer"
                          onClick={() => copyCell(c.kako_id, 'KAKO ID')} title="Copiar">{c.kako_id}</td>
                        <td className="p-3 font-medium">{c.name}</td>
                        <td className="p-3 font-mono text-xs text-blue-600">{c.document_display || '—'}</td>
                        <td className="p-3 text-gray-600">{c.whatsapp}</td>
                        <td className="p-3 text-xs italic text-gray-500 max-w-[220px]">
                          {c.delete_reason || '—'}
                        </td>
                        <td className="p-3 text-gray-500 text-xs">{fmtDate(c.deleted_at)}</td>
                        <td className="p-3 text-center">
                          <Button size="sm" variant="outline" className="h-7 px-2"
                            data-testid={`restore-client-${c.kako_id}`}
                            onClick={() => restoreClient(c)} title="Restaurar cadastro">
                            <RotateCcw className="w-3.5 h-3.5 mr-1" /> Restaurar
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Excluir cadastro do cliente */}
      <Dialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)}>
        <DialogContent className="max-w-md" data-testid="delete-client-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="w-4 h-4" /> Excluir cadastro {deleting?.kako_id}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs leading-relaxed text-amber-900">
              <b>{deleting?.name}</b> sai da lista de clientes e vai para a <b>Lixeira</b>.
              O KAKO ID <b>{deleting?.kako_id}</b> fica livre para um novo cadastro.
              Pedidos, moedas e histórico financeiro <b>não são afetados</b>.
            </div>
            {deleting?.holder_count > 1 && (
              <p className="text-xs text-purple-700" data-testid="delete-client-holder-note">
                Este titular tem {deleting.holder_count} contas KAKO
                ({(deleting.holder_accounts || []).map((h) => h.kako_id).join(', ')}).
                As outras continuam ativas.
              </p>
            )}
            <div>
              <p className="text-xs text-gray-500 mb-1">Motivo da exclusão</p>
              <Select value={delForm.preset || ''}
                onValueChange={(v) => setDelForm({
                  ...delForm, preset: v, reason: v === 'Outro' ? '' : v })}>
                <SelectTrigger data-testid="delete-client-reason-select">
                  <SelectValue placeholder="Escolha o motivo" />
                </SelectTrigger>
                <SelectContent>
                  {MOTIVOS_EXCLUSAO.map((m) => (
                    <SelectItem key={m} value={m} data-testid={`delete-reason-option-${m}`}>{m}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {delForm.preset === 'Outro' && (
                <Input className="mt-2" data-testid="delete-client-reason" value={delForm.reason}
                  placeholder="Descreva o motivo"
                  onChange={(e) => setDelForm({ ...delForm, reason: e.target.value })} />
              )}
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-1">
                Para confirmar, digite o KAKO ID <b>{deleting?.kako_id}</b>
              </p>
              <Input data-testid="delete-client-confirm" value={delForm.confirm}
                inputMode="numeric" placeholder={deleting?.kako_id}
                onChange={(e) => setDelForm({ ...delForm, confirm: e.target.value.replace(/\s/g, '') })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleting(null)} data-testid="delete-client-cancel">
              Cancelar
            </Button>
            <Button onClick={confirmDelete}
              disabled={removing || !delForm.reason.trim()
                || delForm.confirm.trim() !== String(deleting?.kako_id || '')}
              className="bg-red-600 hover:bg-red-700 text-white" data-testid="delete-client-confirm-button">
              {removing ? 'Excluindo...' : 'Excluir cadastro'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Histórico de alterações do cadastro */}
      <Dialog open={!!historyOf} onOpenChange={(o) => !o && setHistoryOf(null)}>
        <DialogContent className="max-w-lg" data-testid="client-history-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="w-4 h-4 text-blue-600" />
              Histórico do cadastro {historyOf?.kako_id}
            </DialogTitle>
          </DialogHeader>
          <div className="max-h-[60vh] space-y-2 overflow-y-auto">
            {loadingHistory && <p className="text-sm text-gray-500">Carregando…</p>}
            {!loadingHistory && history.length === 0 && (
              <p className="text-sm text-gray-500" data-testid="client-history-empty">
                Nenhuma alteração registrada para esta conta.
              </p>
            )}
            {history.map((ev) => (
              <div key={ev.id} className="rounded-lg border border-gray-200 bg-gray-50 p-2.5"
                data-testid="client-history-item">
                <div className="flex items-center justify-between gap-2">
                  <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${
                    ev.actor_type === 'cliente' ? 'bg-blue-100 text-blue-700' : 'bg-amber-100 text-amber-700'}`}>
                    {ev.actor_type === 'cliente' ? 'O PRÓPRIO CLIENTE' : 'ADMIN'}
                  </span>
                  <span className="text-[11px] text-gray-500">{fmtDate(ev.at)}</span>
                </div>
                {ev.note && <p className="mt-1 text-[11px] italic text-gray-500">“{ev.note}”</p>}
                <ul className="mt-1.5 space-y-1">
                  {Object.entries(ev.changes || {}).map(([campo, v]) => (
                    <li key={campo} className="text-xs text-gray-700">
                      <b>{CAMPOS[campo] || campo}</b>:{' '}
                      <span className="text-red-600 line-through">{showVal(v.de)}</span>{' → '}
                      <span className="font-semibold text-green-700">{showVal(v.para)}</span>
                    </li>
                  ))}
                </ul>
                {ev.ip && <p className="mt-1 text-[10px] text-gray-400">IP {ev.ip}</p>}
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setHistoryOf(null)} data-testid="client-history-close">Fechar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Editar dados do cliente (admin) */}
      <Dialog open={!!editClient} onOpenChange={(o) => !o && setEditClient(null)}>
        <DialogContent className="max-w-md" data-testid="edit-client-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pencil className="w-4 h-4 text-green-600" />
              Editar cliente {editClient?.kako_id}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {editClient?.holder_count > 1 && (
              <div className="rounded-lg border border-purple-200 bg-purple-50 p-2.5 text-xs text-purple-800"
                data-testid="edit-client-holder-note">
                <b className="flex items-center gap-1.5">
                  <Users className="h-3.5 w-3.5" /> Titular com {editClient.holder_count} contas KAKO
                </b>
                <p className="mt-1 leading-relaxed">
                  Mesmo CPF/documento em: {(editClient.holder_accounts || []).map((h) => h.kako_id).join(', ')}
                  {' '}— contas de apoio do mesmo cliente. Editar esta não muda as outras.
                </p>
              </div>
            )}
            <div>
              <p className="text-xs text-gray-500 mb-1">Nome completo (MAIÚSCULO, letras, hífen e apóstrofo)</p>
              <Input data-testid="edit-client-name" value={editForm.name || ''} className="uppercase"
                onChange={(e) => setEditForm({ ...editForm, name: e.target.value.toUpperCase().replace(/[^A-ZÀ-ÖØ-Þ \-']/g, '') })} />
            </div>
            <div className="flex items-start gap-2.5 rounded-lg border border-purple-200 bg-purple-50 p-2.5">
              <Checkbox id="admin-foreigner" data-testid="edit-client-foreigner-checkbox"
                checked={!!editForm.is_foreigner} className="mt-0.5"
                onCheckedChange={(v) => setEditForm({ ...editForm, is_foreigner: !!v })} />
              <p className="select-none text-xs leading-relaxed text-purple-800">
                <b>Conta internacional</b> (host estrangeiro sem CPF) — ao salvar, o CPF antigo é
                apagado e ficam valendo país + documento fiscal.
              </p>
            </div>
            {editForm.is_foreigner ? (
              <div className="space-y-3" data-testid="edit-client-foreign-fields">
                <div>
                  <p className="text-xs text-gray-500 mb-1">País</p>
                  <CountrySelect testIdPrefix="edit-client-country" exclude={['Brasil']} value={editForm.country || ''}
                    onChange={(country) => setEditForm({ ...editForm, country })} />
                </div>
                <div>
                  <p className="text-xs text-gray-500 mb-1">
                    {docInfo(fiscalLabels, editForm.country).label}
                    <span className="text-gray-400"> · {docInfo(fiscalLabels, editForm.country).hint}</span>
                  </p>
                  <Input data-testid="edit-client-fiscal-doc" className="uppercase"
                    disabled={!editForm.country}
                    value={editForm.fiscal_document || ''}
                    onChange={(e) => setEditForm({ ...editForm, fiscal_document: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '') })} />
                  {editForm.country && editForm.fiscal_document
                    && !docValid(fiscalLabels, editForm.country, editForm.fiscal_document) && (
                    <p className="mt-1 text-xs font-medium text-red-600" data-testid="edit-client-fiscal-doc-error">
                      {docInfo(fiscalLabels, editForm.country).label} de {editForm.country} inválido
                    </p>
                  )}
                </div>
                <div>
                  <p className="text-xs text-gray-500 mb-1">Nascimento (DD/MM/AAAA)</p>
                  <Input data-testid="edit-client-birth" value={editForm.birth_date || ''} inputMode="numeric"
                    onChange={(e) => setEditForm({ ...editForm, birth_date: e.target.value.replace(/[^\d/]/g, '') })} />
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <p className="text-xs text-gray-500 mb-1">CPF</p>
                  <Input data-testid="edit-client-cpf" value={editForm.cpf || ''} inputMode="numeric"
                    onChange={(e) => setEditForm({ ...editForm, cpf: e.target.value.replace(/[^\d.-]/g, '') })} />
                </div>
                <div>
                  <p className="text-xs text-gray-500 mb-1">Nascimento (DD/MM/AAAA)</p>
                  <Input data-testid="edit-client-birth" value={editForm.birth_date || ''} inputMode="numeric"
                    onChange={(e) => setEditForm({ ...editForm, birth_date: e.target.value.replace(/[^\d/]/g, '') })} />
                </div>
              </div>
            )}
            <div className="grid grid-cols-[90px_1fr] gap-3">
              <div>
                <p className="text-xs text-gray-500 mb-1">DDI</p>
                <Input data-testid="edit-client-dial" value={editForm.whatsapp_dial || ''} inputMode="numeric"
                  onChange={(e) => setEditForm({ ...editForm, whatsapp_dial: e.target.value.replace(/\D/g, '').slice(0, 4) })} />
              </div>
              <div>
                <p className="text-xs text-gray-500 mb-1">WhatsApp (com DDD, sem o DDI)</p>
                <Input data-testid="edit-client-whatsapp" value={editForm.whatsapp || ''} inputMode="numeric"
                  onChange={(e) => setEditForm({ ...editForm, whatsapp: e.target.value.replace(/\D/g, '') })} />
              </div>
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-1">E-mail <span className="text-gray-400">· vazio apaga</span></p>
              <Input data-testid="edit-client-email" type="email" value={editForm.email || ''}
                onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} />
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-1">Apelido no app <span className="text-gray-400">· vazio apaga</span></p>
              <Input data-testid="edit-client-nickname" value={editForm.nickname || ''}
                onChange={(e) => setEditForm({ ...editForm, nickname: e.target.value })} />
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-1">Motivo da correção (opcional)</p>
              <Input data-testid="edit-client-note" value={editForm.note || ''}
                placeholder="Ex: host de Portugal, cadastro feito com CPF do marido"
                onChange={(e) => setEditForm({ ...editForm, note: e.target.value })} />
            </div>
            {editClient && (
              <ClientPasswordCard clientId={editClient.id} authHeaders={authHeaders} />
            )}
            <div className="flex items-center justify-between gap-2">
              <p className="text-[11px] text-gray-400">
                Toda alteração fica registrada com data, autor e IP (auditoria).
              </p>
              <Button size="sm" variant="outline" data-testid="edit-client-history"
                onClick={() => loadHistory(editClient)}>
                <History className="w-3.5 h-3.5 mr-1" /> Histórico
              </Button>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" data-testid="edit-client-delete"
              className="mr-auto border-red-200 text-red-600 hover:bg-red-50"
              onClick={() => { setDeleting(editClient); setDelForm({ confirm: '', reason: '', preset: '' }); setEditClient(null); }}>
              <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Excluir cadastro
            </Button>
            <Button variant="outline" onClick={() => setEditClient(null)} data-testid="edit-client-cancel">Cancelar</Button>
            <Button onClick={saveEdit} disabled={saving}
              className="bg-green-600 hover:bg-green-700 text-white" data-testid="edit-client-save">
              {saving ? 'Salvando...' : 'Salvar alterações'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ClientsManagement;
